import { Component } from 'react'
import { toast } from 'react-toastify'
import { IClientUID } from '../../../api/clients'
import {
    getClientFragmentedSettings,
    updateClientFragmentedSettings,
    restoreClientFragmentedSettings,
    FragmentedSettingType,
    IFragmentedSettings,
} from '../../../api/0x0002/fragmented-settings'
import { getString, getLocale } from '../../../locale'
import Loader from '../../../components/loader'
import TemperatureInput from '../0x0001/temperature.input'
import PressureInput from '../0x0001/pressure.input'
import PercentInput from '../0x0001/percent.input'
import classnames from 'classnames'
import Slider, { Range } from 'rc-slider'
import 'rc-slider/assets/index.css'

type Props = {
    clientId: IClientUID
}

type IFragments = {
    common?: IFragmentedSettings[number]['common']
    sodaUnit?: IFragmentedSettings[number]['sodaUnit']
    aluBlockCoolingUnit?: IFragmentedSettings[number]['aluBlockCoolingUnit']
    iceBlockCoolingUnit?: IFragmentedSettings[number]['iceBlockCoolingUnit']
    ambientUnit?: IFragmentedSettings[number]['ambientUnit']
    circulationPumpUnit?: IFragmentedSettings[number]['circulationPumpUnit']
    heatingUnit?: IFragmentedSettings[number]['heatingUnit']
    shabbatSettings?: IFragmentedSettings[number]['shabbatSettings']
}

type State = {
    fragments?: IFragments
    originalFragments?: IFragments
    settings?: Record<keyof typeof FragmentedSettingType, number>
    updates: Partial<Record<keyof typeof FragmentedSettingType, number>>
    errors: Partial<Record<keyof typeof FragmentedSettingType, string>>
    unsaved: boolean
}

class SettingsTab extends Component<Props, State> {
    state: State = {
        fragments: undefined,
        originalFragments: undefined,
        settings: undefined,
        updates: {},
        errors: {},
        unsaved: false,
    }

    findFragment = (
        systemInfo: IFragmentedSettings,
        prop: keyof IFragmentedSettings[number],
    ): IFragmentedSettings[number][keyof IFragmentedSettings[number]] | undefined => {
        if (systemInfo === undefined) return undefined
        for (const fragmentVersion in systemInfo) {
            const fragment = systemInfo[fragmentVersion]
            if (fragment === undefined) continue
            if (fragment[prop] !== undefined) {
                return fragment[prop]
            }
        }
        return undefined
    }

    componentDidMount = () => {
        this.loadSettings()
    }

    loadSettings = async () => {
        try {
            const fragmentedSettings = (await getClientFragmentedSettings(this.props.clientId)).data

            const fragments: IFragments = {
                common: this.findFragment(fragmentedSettings, 'common') as IFragmentedSettings[number]['common'],
                sodaUnit: this.findFragment(fragmentedSettings, 'sodaUnit') as IFragmentedSettings[number]['sodaUnit'],
                aluBlockCoolingUnit: this.findFragment(
                    fragmentedSettings,
                    'aluBlockCoolingUnit',
                ) as IFragmentedSettings[number]['aluBlockCoolingUnit'],
                iceBlockCoolingUnit: this.findFragment(
                    fragmentedSettings,
                    'iceBlockCoolingUnit',
                ) as IFragmentedSettings[number]['iceBlockCoolingUnit'],
                ambientUnit: this.findFragment(fragmentedSettings, 'ambientUnit') as IFragmentedSettings[number]['ambientUnit'],
                circulationPumpUnit: this.findFragment(
                    fragmentedSettings,
                    'circulationPumpUnit',
                ) as IFragmentedSettings[number]['circulationPumpUnit'],
                heatingUnit: this.findFragment(fragmentedSettings, 'heatingUnit') as IFragmentedSettings[number]['heatingUnit'],
                shabbatSettings: this.findFragment(fragmentedSettings, 'shabbatSettings') as IFragmentedSettings[number]['shabbatSettings'],
            }
            console.log('fragments', fragments)

            let batchedSettings = {} as Record<keyof typeof FragmentedSettingType, number>
            for (const fragmentVersion in fragmentedSettings) {
                const fragment = fragmentedSettings[fragmentVersion] as IFragmentedSettings[number]
                for (const settingName in fragment) {
                    const batch = fragment[settingName as keyof IFragmentedSettings[number]]
                    for (const key in batch) {
                        const value = (batch as any)[key as any]
                        batchedSettings = {
                            ...batchedSettings,
                            [key]: value,
                        }
                    }
                }
            }
            console.log('batchedSettings', batchedSettings)
            this.adjustSettings(batchedSettings)

            this.setState({
                fragments,
                originalFragments: JSON.parse(JSON.stringify(fragments)),
                settings: batchedSettings,
                updates: {},
                errors: {},
                unsaved: false,
            })
        } catch (err) {
            toast.error((err as Error).message)
            console.error(err)
        }
    }

    adjustSettings(settings: Record<keyof typeof FragmentedSettingType, number>) {
        if (settings.pumpMaxTime !== undefined) settings.pumpMaxTime /= 1000
        if (settings.compressorCooldown !== undefined) settings.compressorCooldown /= 1000
        if (settings.oneLiquidLevelDelay !== undefined) settings.oneLiquidLevelDelay /= 1000
        if (settings.pumpStartDelay !== undefined) settings.pumpStartDelay /= 1000
        if (settings.pumpStopDelay !== undefined) settings.pumpStopDelay /= 1000
        if (settings.fanMaxWorkTime !== undefined) settings.fanMaxWorkTime /= 1000
        if (settings.fanCoolTime !== undefined) settings.fanCoolTime /= 1000
        if (settings.circulationInterval !== undefined) settings.circulationInterval /= 1000
        if (settings.circulationWorkTime !== undefined) settings.circulationWorkTime /= 1000
        if (settings.circulationDelayStop !== undefined) settings.circulationDelayStop /= 1000
        if (settings.shabbatHighDelayMinMs !== undefined) settings.shabbatHighDelayMinMs /= 1000
        if (settings.shabbatHighDelayMaxMs !== undefined) settings.shabbatHighDelayMaxMs /= 1000
        if (settings.shabbatLowDelayMinMs !== undefined) settings.shabbatLowDelayMinMs /= 1000
        if (settings.shabbatLowDelayMaxMs !== undefined) settings.shabbatLowDelayMaxMs /= 1000
        if (settings.shabbatLowCsSleepAddMs !== undefined) settings.shabbatLowCsSleepAddMs /= 1000
    }

    normalizeSettings(settings: Partial<Record<keyof typeof FragmentedSettingType, number>>) {
        settings = { ...settings }
        if (settings.pumpMaxTime !== undefined) settings.pumpMaxTime *= 1000
        if (settings.compressorCooldown !== undefined) settings.compressorCooldown *= 1000
        if (settings.oneLiquidLevelDelay !== undefined) settings.oneLiquidLevelDelay *= 1000
        if (settings.pumpStartDelay !== undefined) settings.pumpStartDelay *= 1000
        if (settings.pumpStopDelay !== undefined) settings.pumpStopDelay *= 1000
        if (settings.fanMaxWorkTime !== undefined) settings.fanMaxWorkTime *= 1000
        if (settings.fanCoolTime !== undefined) settings.fanCoolTime *= 1000
        if (settings.circulationInterval !== undefined) settings.circulationInterval *= 1000
        if (settings.circulationWorkTime !== undefined) settings.circulationWorkTime *= 1000
        if (settings.circulationDelayStop !== undefined) settings.circulationDelayStop *= 1000
        if (settings.shabbatHighDelayMinMs !== undefined) settings.shabbatHighDelayMinMs *= 1000
        if (settings.shabbatHighDelayMaxMs !== undefined) settings.shabbatHighDelayMaxMs *= 1000
        if (settings.shabbatLowDelayMinMs !== undefined) settings.shabbatLowDelayMinMs *= 1000
        if (settings.shabbatLowDelayMaxMs !== undefined) settings.shabbatLowDelayMaxMs *= 1000
        if (settings.shabbatLowCsSleepAddMs !== undefined) settings.shabbatLowCsSleepAddMs *= 1000
        if (settings.iceBankMaxWorkTimeMinutes !== undefined) settings.iceBankMaxWorkTimeMinutes = Math.max(1, settings.iceBankMaxWorkTimeMinutes)
        if (settings.iceBankRetryWaitMinutes !== undefined) settings.iceBankRetryWaitMinutes = Math.max(1, settings.iceBankRetryWaitMinutes)
        return settings
    }

    // validateSettings(settings: Partial<Record<keyof typeof FragmentedSettingType, number>>) {
    //     const errors: Partial<Record<keyof typeof FragmentedSettingType, string>> = {}

    //     for (const settingType in FragmentedSettingType) {
    //         const setting = settingType as keyof typeof FragmentedSettingType
    //         const value = settings[setting]
    //         if (value === undefined) continue
    //         switch (setting) {
    //             case 'pumpMaxTime':
    //                 if (value < 0 || value > 2000) errors.pumpMaxTime = 'Pump max time is out of scope'
    //                 break
    //         }
    //     }
    // }

    async saveSettings() {
        try {
            const { updates, errors } = this.state
            if (Object.keys(updates).length === 0) {
                return toast.error(getString('settings.no_updates'))
            }
            if (Object.keys(errors).length > 0) {
                return toast.error(getString('settings.fix_errors'))
            }
            const updatesObj = Object.entries(this.normalizeSettings(updates)).map(([key, value]) => ({
                type: FragmentedSettingType[key as any] as any,
                value: value!,
            }))
            // console.log('updatesObj', updatesObj)
            await updateClientFragmentedSettings(this.props.clientId, updatesObj)
            await this.loadSettings()
            toast.success(getString('common.success', 'common.saved'))
        } catch (err) {
            toast.error((err as Error).message)
            console.error(err)
        }
    }

    async restoreSettings() {
        try {
            await restoreClientFragmentedSettings(this.props.clientId)
            await this.loadSettings()
            toast.success(getString('common.success', 'settings.restored'))
        } catch (err) {
            toast.error((err as Error).message)
            console.error(err)
        }
    }

    handleNumberChange(name: keyof typeof FragmentedSettingType, value: number | string) {
        const { settings, updates, errors } = this.state
        if (typeof value === 'number' || /^\d+$/.test(value)) {
            settings![name] = Number(value)
            updates[name] = Number(value)
            delete errors[name]
        } else {
            errors[name] = getString('settings.must_be_numeric')
            delete updates[name]
        }
        this.setState({ settings, updates, unsaved: true })
    }

    render() {
        if (this.state.settings === undefined) return <Loader size={14} />
        return (
            <div>
                {this.renderActions()}
                {this.renderCategories()}
            </div>
        )
    }

    renderActions() {
        return (
            <div className="grid grid-cols-4 gap-5">
                {this.renderActionButton(getString('common.save'), this.saveSettings.bind(this))}
                {this.renderActionButton(getString('settings.refresh'), this.loadSettings.bind(this))}
                {this.renderActionButton(getString('settings.restore'), this.restoreSettings.bind(this))}
                {this.renderUnsavedChanges()}
            </div>
        )
    }

    renderActionButton(label: string, onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void) {
        return (
            <button
                onClick={onClick}
                className="group py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-501"
            >
                {label}
            </button>
        )
    }

    renderUnsavedChanges() {
        if (!this.state.unsaved) return undefined
        return <div className="dark:text-white flex items-center">* {getString('common.unsaved_changes')}</div>
    }

    renderCategories() {
        return (
            <div className="">
                {this.renderCommonCategory()}
                {this.renderSodaUnitCategory()}
                {this.renderShabbatSettingsCategory()}
                {this.renderAluBlockCoolingUnitCategory()}
                {this.renderIceBankCoolingUnitCategory()}
                {this.renderAmbientUnitCategory()}
                {this.renderCirculationPumpCategory()}
                {this.renderHeatingUnitCategory()}
            </div>
        )
    }

    renderCategoryHeader(label: string) {
        return <h3 className="text-2xl dark:text-white font-semibold text-center">{label}</h3>
    }

    renderInputErrorMessage(message: string) {
        return <div className="text-red-500 text-sm">{message}</div>
    }

    //#region General Category
    renderCommonCategory() {
        return (
            <div className="top-5 bg-white dark:bg-gray-800 rounded-xl shadow-md mt-8 p-6">
                {this.renderCategoryHeader(getString('settings.general_settings'))}
                {this.renderWifiEnable()}
                {this.renderAutoSocketConnect()}
                {this.renderSocketTransportMode()}
                {this.renderHttpPollRateMs()}
                {this.renderLoggingEnabled()}
                {this.renderRemoteLiveLogStreamEnabled()}
            </div>
        )
    }
    renderWifiEnable() {
        const { settings } = this.state
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!!settings!.wifiEnable}
                    onChange={(e: any) => this.handleNumberChange('wifiEnable', e.target.checked ? 1 : 0)}
                />
                <label className="dark:text-white text-sm">{getString('settings.wifi_enable')}</label>
            </div>
        )
    }
    renderAutoSocketConnect() {
        const { settings } = this.state
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!!settings!.autoSocketConnect}
                    onChange={(e: any) => this.handleNumberChange('autoSocketConnect', e.target.checked ? 1 : 0)}
                />
                <label className="dark:text-white text-sm">{getString('settings.auto_socket_connect')}</label>
            </div>
        )
    }

    renderSocketTransportMode() {
        const { settings } = this.state
        if (settings!.socketTransportMode === undefined) return undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm block mb-2">{getString('settings.socket_transport_mode')}</label>
                <select
                    className="pretty-input border-gray-300 dark:bg-gray-700 dark:text-white"
                    value={settings!.socketTransportMode}
                    onChange={(e: any) => this.handleNumberChange('socketTransportMode', Number(e.target.value))}
                >
                    <option value={0}>{getString('settings.socket_transport_mode_tcp')}</option>
                    <option value={1}>{getString('settings.socket_transport_mode_http_polling')}</option>
                </select>
            </div>
        )
    }

    renderHttpPollRateMs() {
        const { settings, errors } = this.state
        if (settings!.httpPollRateMs === undefined) return undefined
        const error = errors.httpPollRateMs !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.http_poll_rate_ms')}</label>
                <input
                    type="number"
                    min={200}
                    max={60000}
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.httpPollRateMs}
                    onInput={(e: any) => this.handleNumberChange('httpPollRateMs', e.target.value)}
                />
                {error && this.renderInputErrorMessage(errors.httpPollRateMs!)}
            </div>
        )
    }

    renderLoggingEnabled() {
        const { settings } = this.state
        if (settings!.loggingEnabled === undefined) return undefined
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!!settings!.loggingEnabled}
                    onChange={(e: any) => this.handleNumberChange('loggingEnabled', e.target.checked ? 1 : 0)}
                />
                <label className="dark:text-white text-sm">{getString('settings.logging_enabled')}</label>
            </div>
        )
    }

    renderRemoteLiveLogStreamEnabled() {
        const { settings } = this.state
        if (settings!.remoteLiveLogStreamEnabled === undefined) return undefined
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!!settings!.remoteLiveLogStreamEnabled}
                    onChange={(e: any) => this.handleNumberChange('remoteLiveLogStreamEnabled', e.target.checked ? 1 : 0)}
                />
                <label className="dark:text-white text-sm">{getString('settings.remote_live_log_stream_enabled')}</label>
            </div>
        )
    }
    //#endregion

    //#region Shabbat Settings Category
    renderShabbatSettingsCategory() {
        const { fragments } = this.state
        if (!fragments?.shabbatSettings) return undefined
        return (
            <div className="top-5 bg-white dark:bg-gray-800 rounded-xl shadow-md mt-8 p-6">
                {this.renderCategoryHeader(getString('settings.shabbat_settings'))}
                {this.renderShabbatModeEnabled()}
                {this.renderShabbatHighDelayMinMs()}
                {this.renderShabbatHighDelayMaxMs()}
                {this.renderShabbatLowDelayMinMs()}
                {this.renderShabbatLowDelayMaxMs()}
                {this.renderShabbatLowCsSleepAddMs()}
                {this.renderShabbatCoolingIntervalMinutes()}
                {this.renderShabbatCoolingCheckMinutes()}
            </div>
        )
    }

    renderShabbatModeEnabled() {
        const { settings } = this.state
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!!settings!.shabbatModeEnabled}
                    onChange={(e: any) => this.handleNumberChange('shabbatModeEnabled', e.target.checked ? 1 : 0)}
                />
                <label className="dark:text-white text-sm">{getString('settings.shabbat_mode_enabled')}</label>
            </div>
        )
    }

    renderShabbatHighDelayMinMs() {
        const { settings, errors } = this.state
        const error = errors.shabbatHighDelayMinMs !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.shabbat_high_delay_min_ms')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.shabbatHighDelayMinMs}
                    onInput={(e: any) => this.handleNumberChange('shabbatHighDelayMinMs', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={120}
                    value={Number(settings!.shabbatHighDelayMinMs)}
                    onChange={value => this.handleNumberChange('shabbatHighDelayMinMs', value)}
                />
                {error && this.renderInputErrorMessage(errors.shabbatHighDelayMinMs!)}
            </div>
        )
    }

    renderShabbatHighDelayMaxMs() {
        const { settings, errors } = this.state
        const error = errors.shabbatHighDelayMaxMs !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.shabbat_high_delay_max_ms')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.shabbatHighDelayMaxMs}
                    onInput={(e: any) => this.handleNumberChange('shabbatHighDelayMaxMs', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={120}
                    value={Number(settings!.shabbatHighDelayMaxMs)}
                    onChange={value => this.handleNumberChange('shabbatHighDelayMaxMs', value)}
                />
                {error && this.renderInputErrorMessage(errors.shabbatHighDelayMaxMs!)}
            </div>
        )
    }

    renderShabbatLowDelayMinMs() {
        const { settings, errors } = this.state
        const error = errors.shabbatLowDelayMinMs !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.shabbat_low_delay_min_ms')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.shabbatLowDelayMinMs}
                    onInput={(e: any) => this.handleNumberChange('shabbatLowDelayMinMs', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={120}
                    value={Number(settings!.shabbatLowDelayMinMs)}
                    onChange={value => this.handleNumberChange('shabbatLowDelayMinMs', value)}
                />
                {error && this.renderInputErrorMessage(errors.shabbatLowDelayMinMs!)}
            </div>
        )
    }

    renderShabbatLowDelayMaxMs() {
        const { settings, errors } = this.state
        const error = errors.shabbatLowDelayMaxMs !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.shabbat_low_delay_max_ms')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.shabbatLowDelayMaxMs}
                    onInput={(e: any) => this.handleNumberChange('shabbatLowDelayMaxMs', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={120}
                    value={Number(settings!.shabbatLowDelayMaxMs)}
                    onChange={value => this.handleNumberChange('shabbatLowDelayMaxMs', value)}
                />
                {error && this.renderInputErrorMessage(errors.shabbatLowDelayMaxMs!)}
            </div>
        )
    }

    renderShabbatLowCsSleepAddMs() {
        const { settings, errors } = this.state
        if (settings!.shabbatLowCsSleepAddMs === undefined) return undefined
        const error = errors.shabbatLowCsSleepAddMs !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.shabbat_low_cs_sleep_add_ms')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.shabbatLowCsSleepAddMs}
                    onInput={(e: any) => this.handleNumberChange('shabbatLowCsSleepAddMs', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={120}
                    value={Number(settings!.shabbatLowCsSleepAddMs)}
                    onChange={value => this.handleNumberChange('shabbatLowCsSleepAddMs', value)}
                />
                {error && this.renderInputErrorMessage(errors.shabbatLowCsSleepAddMs!)}
            </div>
        )
    }

    // Shabbat cooling cycle (LTCB220/LTCB226, kShabbatSettings v3). Values are
    // minutes (no ms conversion) and are validated by the server to 1-120 / 1-60.
    renderShabbatCoolingIntervalMinutes() {
        const { settings, errors } = this.state
        if (settings!.shabbatCoolingIntervalMinutes === undefined) return undefined
        const error = errors.shabbatCoolingIntervalMinutes !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.shabbat_cooling_interval_minutes')}</label>
                <input
                    type="number"
                    min={1}
                    max={120}
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.shabbatCoolingIntervalMinutes}
                    onInput={(e: any) => this.handleNumberChange('shabbatCoolingIntervalMinutes', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={1}
                    max={120}
                    value={Number(settings!.shabbatCoolingIntervalMinutes)}
                    onChange={value => this.handleNumberChange('shabbatCoolingIntervalMinutes', value)}
                />
                {error && this.renderInputErrorMessage(errors.shabbatCoolingIntervalMinutes!)}
            </div>
        )
    }

    renderShabbatCoolingCheckMinutes() {
        const { settings, errors } = this.state
        if (settings!.shabbatCoolingCheckMinutes === undefined) return undefined
        const error = errors.shabbatCoolingCheckMinutes !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.shabbat_cooling_check_minutes')}</label>
                <input
                    type="number"
                    min={1}
                    max={60}
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.shabbatCoolingCheckMinutes}
                    onInput={(e: any) => this.handleNumberChange('shabbatCoolingCheckMinutes', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={1}
                    max={60}
                    value={Number(settings!.shabbatCoolingCheckMinutes)}
                    onChange={value => this.handleNumberChange('shabbatCoolingCheckMinutes', value)}
                />
                {error && this.renderInputErrorMessage(errors.shabbatCoolingCheckMinutes!)}
            </div>
        )
    }
    //#endregion

    //#region Soda Unit Category
    renderSodaUnitCategory() {
        const { fragments, settings } = this.state
        if (!fragments?.sodaUnit) return undefined
        return (
            <div className="top-5 bg-white dark:bg-gray-800 rounded-xl shadow-md mt-8 p-6">
                {this.renderCategoryHeader(getString('settings.pump_settings'))}
                {this.renderLiquidLevelDisabled()}
                {this.renderPumpMaxWorkTime()}
                {this.renderPumpMinWaterPressure()}
                {this.renderPumpMinCo2Pressure()}
                {!settings?.oneLiquidLevel && this.renderPumpStartDelay()}
                {!settings?.oneLiquidLevel && this.renderPumpStopDelay()}
                {this.renderOneLiquidLevel()}
                {this.renderOneLiquidLevelIndicatorIsLow()}
                {this.renderOneLiquidLevelDelay()}
                {this.renderIgnoreH2oPiezo()}
                {this.renderIgnoreCo2Piezo()}
            </div>
        )
    }
    renderLiquidLevelDisabled() {
        const { settings } = this.state
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!settings!.sodaUnitDisabled}
                    onChange={(e: any) => this.handleNumberChange('sodaUnitDisabled', e.target.checked ? 0 : 1)}
                />
                <label className="dark:text-white text-sm">{getString('settings.liquid_level_enabled')}</label>
            </div>
        )
    }
    renderPumpMaxWorkTime() {
        const { settings, errors } = this.state
        const error = errors.pumpMaxTime !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.pump_max_time')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.pumpMaxTime}
                    onInput={(e: any) => this.handleNumberChange('pumpMaxTime', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={2000}
                    value={Number(settings!.pumpMaxTime)}
                    onChange={value => this.handleNumberChange('pumpMaxTime', value)}
                />
                {error && this.renderInputErrorMessage(errors.pumpMaxTime!)}
            </div>
        )
    }
    renderPumpMinWaterPressure() {
        const { settings, errors } = this.state
        const error = errors.minH2oPiezo !== undefined
        return (
            <>
                <PressureInput
                    className="mt-4"
                    label={getString('settings.min_h2o_piezo')}
                    value={settings!.minH2oPiezo}
                    onInput={value => this.handleNumberChange('minH2oPiezo', value)}
                    error={error}
                    errorRenderer={() => error && this.renderInputErrorMessage(errors.minH2oPiezo!)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    value={Number(settings!.minH2oPiezo)}
                    min={1}
                    max={3299}
                    onChange={value => this.handleNumberChange('minH2oPiezo', value)}
                />
            </>
        )
    }
    renderPumpMinCo2Pressure() {
        const { settings, errors } = this.state
        const error = errors.minCo2Piezo !== undefined
        return (
            <>
                <PressureInput
                    className="mt-4"
                    label={getString('settings.min_co2_piezo')}
                    value={settings!.minCo2Piezo}
                    onInput={value => this.handleNumberChange('minCo2Piezo', value)}
                    error={error}
                    errorRenderer={() => error && this.renderInputErrorMessage(errors.minCo2Piezo!)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    value={Number(settings!.minCo2Piezo)}
                    min={1}
                    max={3299}
                    onChange={value => this.handleNumberChange('minCo2Piezo', value)}
                />
            </>
        )
    }
    renderPumpStartDelay() {
        const { settings, errors } = this.state
        const error = errors.pumpStartDelay !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.pump_start_delay')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.pumpStartDelay}
                    onInput={(e: any) => this.handleNumberChange('pumpStartDelay', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={30}
                    value={Number(settings!.pumpStartDelay)}
                    onChange={value => this.handleNumberChange('pumpStartDelay', value)}
                />
                {error && this.renderInputErrorMessage(errors.pumpStartDelay!)}
            </div>
        )
    }
    renderPumpStopDelay() {
        const { settings, errors } = this.state
        const error = errors.pumpStopDelay !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.pump_stop_delay')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.pumpStopDelay}
                    onInput={(e: any) => this.handleNumberChange('pumpStopDelay', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={30}
                    value={Number(settings!.pumpStopDelay)}
                    onChange={value => this.handleNumberChange('pumpStopDelay', value)}
                />
                {error && this.renderInputErrorMessage(errors.pumpStopDelay!)}
            </div>
        )
    }
    renderOneLiquidLevel() {
        const { settings } = this.state
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!!settings!.oneLiquidLevel}
                    onChange={(e: any) => this.handleNumberChange('oneLiquidLevel', e.target.checked ? 1 : 0)}
                />
                <label className="dark:text-white text-sm">{getString('settings.one_liq_lev')}</label>
            </div>
        )
    }
    renderOneLiquidLevelIndicatorIsLow() {
        const { settings } = this.state
        if (!settings!.oneLiquidLevel) return undefined
        return (
            <div className={'flex flex-col items-start mt-4'}>
                <label className="dark:text-white text-sm" htmlFor="oneLiquidLevelIndicatorIsLow">
                    {getString('settings.one_liq_lev_indicator_is_low')}
                </label>
                <select
                    id="oneLiquidLevelIndicatorIsLow"
                    className={classnames('pretty-input', 'pretty-input-rtl')}
                    value={settings!.oneLiquidLevelIndicatorIsLow ?? 0}
                    onChange={e => this.handleNumberChange('oneLiquidLevelIndicatorIsLow', Number(e.target.value))}
                >
                    <option value={1}>{getString('settings.lower_level')}</option>
                    <option value={0}>{getString('settings.higher_level')}</option>
                </select>
            </div>
        )
    }
    renderOneLiquidLevelDelay() {
        const { settings, errors } = this.state
        if (!settings!.oneLiquidLevel) return undefined
        const error = errors.oneLiquidLevelDelay !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.one_liq_lev_delay')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.oneLiquidLevelDelay}
                    onInput={(e: any) => this.handleNumberChange('oneLiquidLevelDelay', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={30}
                    value={Number(settings!.oneLiquidLevelDelay)}
                    onChange={value => this.handleNumberChange('oneLiquidLevelDelay', value)}
                />
                {error && this.renderInputErrorMessage(errors.oneLiquidLevelDelay!)}
            </div>
        )
    }
    renderIgnoreH2oPiezo() {
        const { settings } = this.state
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!!settings!.ignoreH2oPiezo}
                    onChange={(e: any) => this.handleNumberChange('ignoreH2oPiezo', e.target.checked ? 1 : 0)}
                />
                <label className="dark:text-white text-sm">{getString('settings.ignore_h2o_piezo')}</label>
            </div>
        )
    }
    renderIgnoreCo2Piezo() {
        const { settings } = this.state
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!!settings!.ignoreCo2Piezo}
                    onChange={(e: any) => this.handleNumberChange('ignoreCo2Piezo', e.target.checked ? 1 : 0)}
                />
                <label className="dark:text-white text-sm">{getString('settings.ignore_co2_piezo')}</label>
            </div>
        )
    }
    //#endregion

    //#region AluBlock Cooling Unit Category
    renderAluBlockCoolingUnitCategory() {
        const { fragments } = this.state
        if (!fragments?.aluBlockCoolingUnit) return undefined
        return (
            <div className="top-5 bg-white dark:bg-gray-800 rounded-xl shadow-md mt-8 p-6">
                {this.renderCategoryHeader(getString('settings.cooling_unit_settings'))}
                {this.renderAluBlockCoolingUnitEnable()}
                {this.renderNtc1Range()}
                {this.renderCompressorCooldown()}
            </div>
        )
    }
    renderAluBlockCoolingUnitEnable() {
        const { settings } = this.state
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!settings!.coolingUnitDisabled}
                    onChange={(e: any) => this.handleNumberChange('coolingUnitDisabled', e.target.checked ? 0 : 1)}
                />
                <label className="dark:text-white text-sm">{getString('settings.cooling_unit_enabled')}</label>
            </div>
        )
    }
    renderNtc1Range() {
        const { settings } = this.state
        return (
            <>
                <div className="mt-4 grid grid-cols-2 gap-8">
                    {this.renderNtc1MinValue()}
                    {this.renderNtc1MaxValue()}
                </div>
                <Range
                    reverse={getLocale() !== 'he'}
                    value={[Number(settings!.ntc1MaxValue), Number(settings!.ntc1MinValue)]}
                    min={1}
                    max={3299}
                    onChange={value => {
                        this.handleNumberChange('ntc1MaxValue', value[0])
                        this.handleNumberChange('ntc1MinValue', value[1])
                    }}
                />
            </>
        )
    }
    renderNtc1MaxValue() {
        const { settings, errors } = this.state
        const error = errors.ntc1MaxValue !== undefined
        return (
            <TemperatureInput
                className="mt-4"
                label={getString('settings.ntc1_max_value')}
                value={settings!.ntc1MaxValue}
                onInput={value => this.handleNumberChange('ntc1MaxValue', value)}
                error={error}
                errorRenderer={() => error && this.renderInputErrorMessage(errors.ntc1MaxValue!)}
            />
        )
    }
    renderNtc1MinValue() {
        const { settings, errors } = this.state
        const error = errors.ntc1MinValue !== undefined
        return (
            <TemperatureInput
                className="mt-4"
                label={getString('settings.ntc1_min_value')}
                value={settings!.ntc1MinValue}
                onInput={value => this.handleNumberChange('ntc1MinValue', value)}
                error={error}
                errorRenderer={() => error && this.renderInputErrorMessage(errors.ntc1MinValue!)}
            />
        )
    }
    renderCompressorCooldown() {
        const { settings, errors } = this.state
        const error = errors.compressorCooldown !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.compressor_cooldown')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.compressorCooldown}
                    onInput={(e: any) => this.handleNumberChange('compressorCooldown', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={2000}
                    value={Number(settings!.compressorCooldown)}
                    onChange={value => this.handleNumberChange('compressorCooldown', value)}
                />
                {error && this.renderInputErrorMessage(errors.compressorCooldown!)}
            </div>
        )
    }
    //#endregion
    //#region IceBank Cooling Unit Category
    renderIceBankCoolingUnitCategory() {
        const { fragments } = this.state
        if (!fragments?.iceBlockCoolingUnit) return undefined
        return (
            <div className="top-5 bg-white dark:bg-gray-800 rounded-xl shadow-md mt-8 p-6">
                {this.renderCategoryHeader(getString('settings.cooling_unit_settings'))}
                {this.renderIceBankCoolingUnitEnable()}
                {this.renderWaterValue()}
                {this.renderIceValue()}
                {this.renderRelayCooldown()}
                {this.renderIceBankMaxWorkTimeMinutes()}
                {this.renderIceBankRetryWaitMinutes()}
            </div>
        )
    }
    renderIceBankCoolingUnitEnable() {
        const { settings } = this.state
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!settings!.coolingUnitDisabled}
                    onChange={(e: any) => this.handleNumberChange('coolingUnitDisabled', e.target.checked ? 0 : 1)}
                />
                <label className="dark:text-white text-sm">{getString('settings.cooling_unit_enabled')}</label>
            </div>
        )
    }
    renderWaterValue() {
        const { settings, errors } = this.state
        const error = errors.waterValue !== undefined
        return (
            <PercentInput
                className="mt-4"
                label={getString('settings.water_value')}
                value={settings!.waterValue}
                onInput={value => this.handleNumberChange('waterValue', value)}
                error={error}
                errorRenderer={() => error && this.renderInputErrorMessage(errors.waterValue!)}
            />
        )
    }
    renderIceValue() {
        const { settings, errors } = this.state
        const error = errors.iceValue !== undefined
        return (
            <PercentInput
                className="mt-4"
                label={getString('settings.ice_value')}
                value={settings!.iceValue}
                onInput={value => this.handleNumberChange('iceValue', value)}
                error={error}
                errorRenderer={() => error && this.renderInputErrorMessage(errors.iceValue!)}
            />
        )
    }
    renderRelayCooldown() {
        const { settings, errors } = this.state
        const error = errors.compressorCooldown !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.compressor_cooldown')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.compressorCooldown}
                    onInput={(e: any) => this.handleNumberChange('compressorCooldown', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={2000}
                    value={Number(settings!.compressorCooldown)}
                    onChange={value => this.handleNumberChange('compressorCooldown', value)}
                />
                {error && this.renderInputErrorMessage(errors.compressorCooldown!)}
            </div>
        )
    }

    renderIceBankMaxWorkTimeMinutes() {
        const { settings, errors } = this.state
        if (settings!.iceBankMaxWorkTimeMinutes === undefined) return undefined
        const error = errors.iceBankMaxWorkTimeMinutes !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.icebank_max_work_time_minutes')}</label>
                <input
                    type="number"
                    min={1}
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.iceBankMaxWorkTimeMinutes}
                    onInput={(e: any) => this.handleNumberChange('iceBankMaxWorkTimeMinutes', e.target.value)}
                />
                {error && this.renderInputErrorMessage(errors.iceBankMaxWorkTimeMinutes!)}
            </div>
        )
    }

    renderIceBankRetryWaitMinutes() {
        const { settings, errors } = this.state
        if (settings!.iceBankRetryWaitMinutes === undefined) return undefined
        const error = errors.iceBankRetryWaitMinutes !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.icebank_retry_wait_minutes')}</label>
                <input
                    type="number"
                    min={1}
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.iceBankRetryWaitMinutes}
                    onInput={(e: any) => this.handleNumberChange('iceBankRetryWaitMinutes', e.target.value)}
                />
                {error && this.renderInputErrorMessage(errors.iceBankRetryWaitMinutes!)}
            </div>
        )
    }
    //#endregion

    //#region Ambient Unit Category
    renderAmbientUnitCategory() {
        const { fragments } = this.state
        if (!fragments?.ambientUnit) return undefined
        return (
            <div className="top-5 bg-white dark:bg-gray-800 rounded-xl shadow-md mt-8 p-6">
                {this.renderCategoryHeader(getString('settings.ambient_unit_settings'))}
                {this.renderAmbientUnitEnable()}
                {this.renderNtc2Range()}
                {this.renderFanMaxWorkTime()}
                {this.renderFanCoolTime()}
            </div>
        )
    }
    renderAmbientUnitEnable() {
        const { settings } = this.state
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!!settings!.ambientUnitEnabled}
                    onChange={(e: any) => this.handleNumberChange('ambientUnitEnabled', e.target.checked ? 1 : 0)}
                />
                <label className="dark:text-white text-sm">{getString('settings.ambient_monitor_enabled')}</label>
            </div>
        )
    }
    renderNtc2Range() {
        const { settings } = this.state
        return (
            <>
                <div className="mt-4 grid grid-cols-2 gap-8">
                    {this.renderNtc2MinValue()}
                    {this.renderNtc2MaxValue()}
                </div>
                <Range
                    reverse={getLocale() !== 'he'}
                    value={[Number(settings!.ntc2MaxValue), Number(settings!.ntc2MinValue)]}
                    min={1}
                    max={3299}
                    onChange={value => {
                        this.handleNumberChange('ntc2MaxValue', value[0])
                        this.handleNumberChange('ntc2MinValue', value[1])
                    }}
                />
            </>
        )
    }
    renderNtc2MaxValue() {
        const { settings, errors } = this.state
        const error = errors.ntc2MaxValue !== undefined
        return (
            <TemperatureInput
                className="mt-4"
                label={getString('settings.ntc2_max_value')}
                value={settings!.ntc2MaxValue}
                onInput={value => this.handleNumberChange('ntc2MaxValue', value)}
                error={error}
                errorRenderer={() => error && this.renderInputErrorMessage(errors.ntc2MaxValue!)}
            />
        )
    }
    renderNtc2MinValue() {
        const { settings, errors } = this.state
        const error = errors.ntc2MinValue !== undefined
        return (
            <TemperatureInput
                className="mt-4"
                label={getString('settings.ntc2_min_value')}
                value={settings!.ntc2MinValue}
                onInput={value => this.handleNumberChange('ntc2MinValue', value)}
                error={error}
                errorRenderer={() => error && this.renderInputErrorMessage(errors.ntc2MinValue!)}
            />
        )
    }
    renderFanMaxWorkTime() {
        const { settings, errors } = this.state
        const error = errors.fanMaxWorkTime !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.fan_max_work_time')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.fanMaxWorkTime}
                    onInput={(e: any) => this.handleNumberChange('fanMaxWorkTime', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={2000}
                    value={Number(settings!.fanMaxWorkTime)}
                    onChange={value => this.handleNumberChange('fanMaxWorkTime', value)}
                />
                {error && this.renderInputErrorMessage(errors.fanMaxWorkTime!)}
            </div>
        )
    }
    renderFanCoolTime() {
        const { settings, errors } = this.state
        const error = errors.fanCoolTime !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.fan_cool_time')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.fanCoolTime}
                    onInput={(e: any) => this.handleNumberChange('fanCoolTime', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={2000}
                    value={Number(settings!.fanCoolTime)}
                    onChange={value => this.handleNumberChange('fanCoolTime', value)}
                />
                {error && this.renderInputErrorMessage(errors.fanCoolTime!)}
            </div>
        )
    }
    //#endregion

    //#region Circulation Pump Category
    renderCirculationPumpCategory() {
        const { fragments } = this.state
        if (!fragments?.circulationPumpUnit) return undefined
        return (
            <div className="top-5 bg-white dark:bg-gray-800 rounded-xl shadow-md mt-8 p-6">
                {this.renderCategoryHeader(getString('settings.circulation_pump_settings'))}
                {this.renderCirculationEnabled()}
                {this.renderCirculationDelayStop()}
                {this.renderCirculationInterval()}
                {this.renderCirculationWorkTime()}
            </div>
        )
    }
    renderCirculationEnabled() {
        const { settings } = this.state
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!!settings!.circulationPumpUnitEnabled}
                    onChange={(e: any) => this.handleNumberChange('circulationPumpUnitEnabled', e.target.checked ? 1 : 0)}
                />
                <label className="dark:text-white text-sm">{getString('settings.circulation_enabled')}</label>
            </div>
        )
    }
    renderCirculationDelayStop() {
        const { settings, errors } = this.state
        const error = errors.circulationDelayStop !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.circulation_delay_stop')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.circulationDelayStop}
                    onInput={(e: any) => this.handleNumberChange('circulationDelayStop', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={2000}
                    value={Number(settings!.circulationDelayStop)}
                    onChange={value => this.handleNumberChange('circulationDelayStop', value)}
                />
                {error && this.renderInputErrorMessage(errors.circulationDelayStop!)}
            </div>
        )
    }
    renderCirculationInterval() {
        const { settings, errors } = this.state
        const error = errors.circulationInterval !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.circulation_interval')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.circulationInterval}
                    onInput={(e: any) => this.handleNumberChange('circulationInterval', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={86400}
                    value={Number(settings!.circulationInterval)}
                    onChange={value => this.handleNumberChange('circulationInterval', value)}
                />
                {error && this.renderInputErrorMessage(errors.circulationInterval!)}
            </div>
        )
    }
    renderCirculationWorkTime() {
        const { settings, errors } = this.state
        const error = errors.circulationWorkTime !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.circulation_work_time')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.circulationWorkTime}
                    onInput={(e: any) => this.handleNumberChange('circulationWorkTime', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={2000}
                    value={Number(settings!.circulationWorkTime)}
                    onChange={value => this.handleNumberChange('circulationWorkTime', value)}
                />
                {error && this.renderInputErrorMessage(errors.circulationWorkTime!)}
            </div>
        )
    }
    //#endregion

    //#region HeatingUnit Category
    renderHeatingUnitCategory() {
        const { fragments, settings } = this.state
        if (!fragments?.heatingUnit) return undefined
        return (
            <div className="top-5 bg-white dark:bg-gray-800 rounded-xl shadow-md mt-8 p-6">
                {this.renderCategoryHeader(getString('settings.boiler_settings'))}
                {this.renderBoilerEnable()}
                <div className="grid grid-cols-2 gap-8">
                    {this.renderBoilerMinHeat()}
                    {this.renderBoilerMaxHeat()}
                </div>
                <Range
                    reverse={getLocale() !== 'he'}
                    value={[Number(settings!.boilerMaxHeat), Number(settings!.boilerMinHeat)]}
                    min={1}
                    max={3299}
                    onChange={value => {
                        this.handleNumberChange('boilerMinHeat', value[1])
                        this.handleNumberChange('boilerMaxHeat', value[0])
                    }}
                />
            </div>
        )
    }
    renderBoilerEnable() {
        const { settings } = this.state
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!!settings!.heatingUnitEnabled}
                    onChange={(e: any) => this.handleNumberChange('heatingUnitEnabled', e.target.checked ? 1 : 0)}
                />
                <label className="dark:text-white text-sm">{getString('settings.boiler_enable')}</label>
            </div>
        )
    }
    renderBoilerMaxHeat() {
        const { settings, errors } = this.state
        const error = errors.boilerMaxHeat !== undefined
        return (
            <TemperatureInput
                className="mt-4"
                label={getString('settings.boiler_max_heat')}
                value={settings!.boilerMaxHeat}
                onInput={value => this.handleNumberChange('boilerMaxHeat', value)}
                error={error}
                errorRenderer={() => error && this.renderInputErrorMessage(errors.boilerMaxHeat!)}
            />
        )
    }
    renderBoilerMinHeat() {
        const { settings, errors } = this.state
        const error = errors.boilerMinHeat !== undefined
        return (
            <TemperatureInput
                className="mt-4"
                label={getString('settings.boiler_min_heat')}
                value={settings!.boilerMinHeat}
                onInput={value => this.handleNumberChange('boilerMinHeat', value)}
                error={error}
                errorRenderer={() => error && this.renderInputErrorMessage(errors.boilerMinHeat!)}
            />
        )
    }
    //#endregion
}

export default SettingsTab
